import React, { useEffect, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import './carousel-base.css';
import './carousel.css';

const getHref = (link) => {
  const item = Array.isArray(link) ? link[0] : link;
  if (!item || typeof item !== 'object') return '#';
  if (item['@type'] === 'Link' && item.remoteUrl) return item.remoteUrl;
  const url = item['@id'] || '';
  const isFile = item['@type'] === 'File';
  return url ? (isFile ? `${url}/@@download/file` : url) : '#';
};

const EmblaRatings = ({ data, id }) => {
  const slides = data.slides || [];
  const slidesToShow = parseInt(data.slidesToShow, 10) || 1;

  const [effectiveSlidesToShow, setEffectiveSlidesToShow] = useState(slidesToShow); // Use slidesToShow as initial value
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true); // Mark that we're now on the client
  }, []);

  useEffect(() => {
    if (!isClient) return; // Don't run until we're on the client
    
    const calculateSlidesToShow = () => {
      const isMobile = window.matchMedia('(max-width: 768px)').matches;
      setEffectiveSlidesToShow(isMobile ? 1 : slidesToShow);
    };
    
    calculateSlidesToShow();
    
    window.addEventListener('resize', calculateSlidesToShow);
    return () => window.removeEventListener('resize', calculateSlidesToShow);
  }, [slidesToShow, isClient]);

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [emblaRef, embla] = useEmblaCarousel({
    align: 'start',
    loop: data.loop ?? true,
    slidesToScroll: effectiveSlidesToShow,
  }, data.autoplay ? [Autoplay({ delay: parseInt(data.autoplayDelay, 10) || 4000, stopOnInteraction: false })] : []);

  useEffect(() => {
    if (!embla) return;
    embla.on('select', () => setSelectedIndex(embla.selectedScrollSnap()));
  }, [embla]);

  const scrollPrev = () => embla && embla.scrollPrev();
  const scrollNext = () => embla && embla.scrollNext();
  const scrollTo = (index) => embla && embla.scrollTo(index);

  const renderStars = (rating) => {
    if (!rating || rating < 1) return null;
    const count = Math.min(5, Math.max(1, rating));
    return (
      <div className="stars">
        {'★'.repeat(count)}{'☆'.repeat(5 - count)}
      </div>
    );
  };

  const renderSlideContent = (slide) => {
    const imageUrl = slide.image ? (Array.isArray(slide.image) ? slide.image[0]?.['@id'] : slide.image?.['@id']) : null;
    
    return (
      <>
        {imageUrl && (
          <img 
            src={`${imageUrl}/@@images/image/preview`} 
            alt={slide.author || 'Review image'} 
            className="review-image"
          />
        )}
        {renderStars(slide.rating)}
        <p>{slide.testimonial}</p>
        <strong>{slide.author}</strong>
        {slide.company && <div>{slide.company}</div>}
      </>
    );
  };

  return (
    <div className={`embla embla-ratings ${data.isFullWidth ? 'full-width' : ''} align-${data.alignment || 'left'} valign-${data.verticalAlignment || 'middle'}`}>
      {data.hideArrows !== true && slides.length > 1 && (
        <>
          <button className="embla__prev" onClick={scrollPrev}>‹</button>
          <button className="embla__next" onClick={scrollNext}>›</button>
        </>
      )}

      <div className="embla__viewport" ref={emblaRef}>
        <div className="embla__container">
          {slides.map((slide, index) => {
            const href = getHref(slide.link);
            const hasLink = href && href !== '#';
            const isExternal = hasLink && href.startsWith('http') && isClient && !href.includes(window.location.hostname);
            
            const slideContent = (
              <div className={`embla-ratings-slide-inner ${hasLink ? 'clickable' : ''}`}>
                {renderSlideContent(slide)}
              </div>
            );

            return (
              <div className="embla__slide" key={index} style={{ flex: `0 0 ${100 / effectiveSlidesToShow}%` }}>
                {hasLink ? (
                  <a
                    href={href}
                    style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}
                    {...(isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                  >
                    {slideContent}
                  </a>
                ) : (
                  slideContent
                )}
              </div>
            );
          })}
        </div>
      </div>

      {data.hideDots !== true && slides.length > 1 && (
        <div className="embla__nav">
          {Array.from({ length: Math.ceil(slides.length / effectiveSlidesToShow) }).map((_, i) => (
            <button
              key={i}
              onClick={() => scrollTo(i)}
              className={`embla__dot ${i === selectedIndex ? 'is-selected' : ''}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default EmblaRatings;